import type React from "react";
import { useCallback, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slider,
  Stack,
  Typography,
} from "@mui/material";
import Cropper, { type Area } from "react-easy-crop";
import { useTranslation } from "react-i18next";
import type translations from "../../translations/translations.json";
import {
  getCroppedProfilePictureBlob,
  PROFILE_PICTURE_OUTPUT_MIME,
} from "../../utils/profilePictureCrop";

type TranslationKeys = keyof typeof translations.en;

interface ProfilePictureCropDialogProps {
  open: boolean;
  imageSrc: string;
  onCancel: () => void;
  onConfirm: (file: File) => void;
}

const ProfilePictureCropDialog: React.FC<ProfilePictureCropDialogProps> = ({
  open,
  imageSrc,
  onCancel,
  onConfirm,
}) => {
  const { t } = useTranslation();
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [processing, setProcessing] = useState(false);

  const onCropComplete = useCallback((_croppedArea: Area, croppedPixels: Area) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleConfirm = async () => {
    if (!croppedAreaPixels) {
      return;
    }

    setProcessing(true);
    try {
      const blob = await getCroppedProfilePictureBlob(imageSrc, croppedAreaPixels, rotation);
      const file = new File([blob], "profile-picture.jpg", {
        type: PROFILE_PICTURE_OUTPUT_MIME,
        lastModified: Date.now(),
      });
      onConfirm(file);
    } finally {
      setProcessing(false);
    }
  };

  const handleReset = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>{t("profile.picture.adjustTitle" as TranslationKeys)}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("profile.picture.adjustHint" as TranslationKeys)}
        </Typography>
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: 320,
            bgcolor: "grey.900",
            borderRadius: 1,
            overflow: "hidden",
          }}
        >
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            rotation={rotation}
            aspect={1}
            cropShape="round"
            showGrid={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onRotationChange={setRotation}
            onCropComplete={onCropComplete}
          />
        </Box>
        <Stack spacing={2} sx={{ mt: 3 }}>
          <Box>
            <Typography variant="caption" color="text.secondary" gutterBottom display="block">
              {t("profile.picture.zoom" as TranslationKeys)}
            </Typography>
            <Slider
              value={zoom}
              min={1}
              max={3}
              step={0.01}
              onChange={(_event, value) => setZoom(value as number)}
              aria-label={t("profile.picture.zoom" as TranslationKeys)}
            />
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" gutterBottom display="block">
              {t("profile.picture.rotation" as TranslationKeys)}
            </Typography>
            <Slider
              value={rotation}
              min={0}
              max={360}
              step={1}
              onChange={(_event, value) => setRotation(value as number)}
              aria-label={t("profile.picture.rotation" as TranslationKeys)}
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleReset} disabled={processing}>
          {t("profile.picture.reset" as TranslationKeys)}
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onCancel} disabled={processing}>
          {t("Cancel" as TranslationKeys)}
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={processing || !croppedAreaPixels}
        >
          {t("profile.picture.apply" as TranslationKeys)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProfilePictureCropDialog;
